import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Calendar, Tag, ArrowLeft, MessageCircle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { publicApi } from "../lib/api";
import { useSettings, buildWaLink } from "../lib/SettingsContext";
import { trackWhatsAppLead } from "../lib/AnalyticsInjector";
import { SITE } from "../data/constants";
import SEO from "../components/SEO";
import SchemaMarkup from "../components/SchemaMarkup";
import Picture from "../components/Picture";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const { settings } = useSettings();

  useEffect(() => {
    setLoading(true);
    publicApi
      .getPost(slug)
      .then(setPost)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (notFound) return <Navigate to="/blog" replace />;
  if (loading || !post) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-12 py-24">
        <p className="text-sm text-[#86868B]">Memuat artikel…</p>
      </div>
    );
  }

  // Replace placeholder tokens in content with live settings
  const content = (post.content || "")
    .replace(/\{SALES_NAME\}/g, settings.sales_name)
    .replace(/\{WA_NUMBER\}/g, settings.sales_phone);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.seo_description || post.excerpt,
    image: post.cover_image,
    datePublished: post.created_at,
    dateModified: post.updated_at,
    author: { "@type": "Person", name: post.author || "Kiel" },
    publisher: {
      "@type": "Organization",
      name: settings.dealer_name,
    },
    mainEntityOfPage: `${SITE.domain}/blog/${post.slug}`,
  };

  return (
    <>
      <SEO
        title={post.seo_title || `${post.title} | Blog BYD Cibubur`}
        description={post.seo_description || post.excerpt}
        canonical={`${SITE.domain}/blog/${post.slug}`}
        ogImage={post.cover_image}
      />
      <SchemaMarkup id={`article-${post.slug}`} schema={articleSchema} />

      <article className="max-w-3xl mx-auto px-6 lg:px-12 pt-12 pb-20">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-sm text-[#6E6E73] hover:text-[#0F0F0F] mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Semua Artikel
        </Link>

        <div className="flex items-center gap-3 text-xs text-[#86868B] mb-5">
          <span className="inline-flex items-center gap-1.5">
            <Tag className="w-3.5 h-3.5" />
            {post.category}
          </span>
          <span>•</span>
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            {new Date(post.created_at).toLocaleDateString("id-ID", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </span>
        </div>

        <h1 className="text-4xl sm:text-5xl font-medium tracking-tight text-[#0F0F0F] leading-[1.1]">
          {post.title}
        </h1>
        <p className="mt-6 text-lg text-[#6E6E73] leading-relaxed">
          {post.excerpt}
        </p>

        <div className="mt-10 aspect-[16/9] rounded-3xl overflow-hidden bg-[#F5F5F7] border border-[#E5E5EA]">
          <Picture
            src={post.cover_image}
            alt={post.title}
            className="w-full h-full object-cover"
            width={1600}
            loading="eager"
          />
        </div>

        <div className="prose prose-neutral max-w-none mt-12 text-[#0F0F0F] [&_h2]:text-2xl [&_h2]:font-medium [&_h2]:tracking-tight [&_h2]:mt-12 [&_h2]:mb-4 [&_h3]:text-xl [&_h3]:font-medium [&_h3]:mt-8 [&_h3]:mb-3 [&_p]:text-base [&_p]:leading-relaxed [&_p]:text-[#1A1A1A] [&_p]:my-5 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2 [&_li]:text-[#1A1A1A] [&_strong]:text-[#0F0F0F] [&_strong]:font-semibold">
          <ReactMarkdown>{content}</ReactMarkdown>
        </div>

        <div className="mt-16 p-8 rounded-3xl bg-[#0F0F0F] text-white">
          <p className="text-xs uppercase tracking-[0.25em] text-white/60 mb-3">
            Konsultasi Lanjut
          </p>
          <h3 className="text-2xl font-medium tracking-tight">
            Punya pertanyaan tentang artikel ini?
          </h3>
          <p className="mt-3 text-sm text-white/70 max-w-md">
            Hubungi {settings.sales_name} langsung untuk diskusi spesifik
            kebutuhan Anda — termasuk hitungan kredit dan jadwal test drive.
          </p>
          <a
            href={buildWaLink(settings)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWhatsAppLead("blog-detail")}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white px-6 py-3.5 text-sm font-medium transition-colors"
            data-testid={`blog-cta-${post.slug}`}
          >
            <MessageCircle className="w-4 h-4" />
            {settings.wa_cta_blog || settings.wa_cta_text}
          </a>
        </div>
      </article>
    </>
  );
}
